import React, { useEffect, useRef, useCallback } from 'react';
import EditorJS from '@editorjs/editorjs';
import type { OutputData } from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import ImageTool from '@editorjs/image';
import Paragraph from '@editorjs/paragraph';
import Quote from '@editorjs/quote';
import Marker from '@editorjs/marker';
import Checklist from '@editorjs/checklist';
import Delimiter from '@editorjs/delimiter';
import Table from '@editorjs/table';
import Warning from '@editorjs/warning';
import Code from '@editorjs/code';
import LinkTool from '@editorjs/link';
import Raw from '@editorjs/raw';
import { Box } from '@mui/material';
import './EditorJS.css';

interface EditorJSProps {
  data?: OutputData;
  onChange?: (data: OutputData) => void;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
}

const EditorJSComponent: React.FC<EditorJSProps> = ({
  data,
  onChange,
  disabled = false,
  placeholder = 'Start writing your content...',
  minHeight = 400,
}) => {
  const editorRef = useRef<EditorJS | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isReadyRef = useRef<boolean>(false);

  // Memoized change handler to prevent re-creation on every render
  const handleChange = useCallback(async () => {
    if (editorRef.current && onChange) {
      try {
        const outputData = await editorRef.current.save();
        onChange(outputData);
      } catch (error) {
        console.error('Error saving editor data:', error);
      }
    }
  }, [onChange]);

  // Main initialization and cleanup effect
  useEffect(() => {
    // Only initialize if there's a holder element and no existing editor instance
    if (containerRef.current && !editorRef.current && !isReadyRef.current) {
      // Mark as initalizing
      isReadyRef.current = true;
      const editor = new EditorJS({
        holder: containerRef.current,
        data: data || { blocks: [] },
        placeholder,
        readOnly: disabled,
        tools: {
          header: Header,
          list: List,
          image: {
            class: ImageTool,
            config: {
                // IMPORTANT: You must implement your own server-side uploader.
                // This is a placeholder that returns a static image.
                uploader: {
                    uploadByFile(file){
                        console.log('Uploading file:', file);
                        // Replace this with your actual file upload logic that
                        // sends the file to your server and returns the URL.
                        return new Promise((resolve) => {
                            resolve({
                                success: 1,
                                file: {
                                    url: 'https://placehold.co/600x400/EEE/31343C?text=Placeholder',
                                }
                            });
                        });
                    },
                    uploadByUrl(url) {
                        // Logic to upload an image by its URL
                        return new Promise((resolve) => {
                             resolve({
                                success: 1,
                                file: {
                                    url: url,
                                }
                            });
                        })
                    }
                }
            }
          },
          // paragraph: {
          //   class: Paragraph,
          //   inlineToolbar: true,
          // },
          paragraph: Paragraph,
          quote: Quote,
          marker: Marker,
          checklist: Checklist,
          delimiter: Delimiter,
          table: Table,
          warning: Warning,
          code: Code,
          linkTool: LinkTool,
          raw: Raw,
        },
        onChange: handleChange,
        onReady: () => {
          console.log('Editor.js is ready to work!');
        },
      });
      editorRef.current = editor;
    }

    // Cleanup function
    return () => {
      // On cleanup, ensure the editor instance is ready before destroying
      if (editorRef.current && typeof editorRef.current.destroy === 'function') {
          editorRef.current.destroy();
          editorRef.current = null;
      }
    };
  }, []); // Empty dependency array ensures this effect runs once on mount.

  // Effect to handle external data changes
  // useEffect(() => {
  //   if (editorRef.current && data) {
  //     editorRef.current.isReady
  //       .then(() => {
  //           if (editorRef.current?.render) {
  //               editorRef.current.render(data);
  //           }
  //       })
  //       .catch(error => console.error("Error on data update:", error));
  //   }
  // }, [data]);

  // Effect to handle read-only state changes
  useEffect(() => {
    if (editorRef.current) {
       editorRef.current.isReady
        .then(() => {
            if (editorRef.current?.readOnly) {
                editorRef.current.readOnly.toggle(disabled);
            }
        })
        .catch(error => console.error("Error on toggling read-only:", error));
    }
  }, [disabled]);

  return (
    <Box
      ref={containerRef}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        minHeight,
        '& .codex-editor': {
            // General styling for the editor container
        },
        '& .codex-editor__redactor': {
          paddingBottom: '100px !important',
        },
        '& .codex-editor .ce-block__content, & .codex-editor .ce-toolbar__content': {
          maxWidth: '100%',
        },
        '& .codex-editor--read-only .ce-toolbar__actions': {
            display: 'none',
        },
      }}
    />
  );
};

export default EditorJSComponent;
